import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Layers, Loader2, Pencil, Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AuthService } from '@/services/core/auth'
import { apiErrorDescription, ApiError } from '@/services/core/apiError'
import {
  TIER_TYPES,
  TiersApi,
  type TierDetail,
  type TierInput,
  type TierType,
} from '@/services/networks/tiersApi'
import { formatDate, formatNumber } from '@/utils/format'
import { AmountInput } from '../../components/AmountInput'
import { AppShell } from '../../components/AppShell'
import { DetailList, DetailRow } from '../../components/DetailList'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { TierEnabledChip } from '../../components/TierPriorityTable'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { useNetwork } from '../../contexts/NetworkContext'
import { useEditMode } from '../../hooks/useEditMode'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

/** A whole percentage from 1 to 100, as the API accepts for traffic shares. */
function isTrafficPercentage(value: string) {
  const n = Number(value)
  return value.trim() !== '' && Number.isInteger(n) && n >= 1 && n <= 100
}

// ── Inner form component ────────────────────────────────────────────────────

interface TierEditFormProps {
  tier: TierDetail | null
  id: string
  isNew: boolean
}

function TierEditForm({ tier, id, isNew }: TierEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const { isEditing, startEditing, stopEditing } = useEditMode(isNew)
  const enabled = !!currentNetwork?.tiers.some((networkTier) => networkTier.id === id)

  const form = useForm({
    defaultValues: {
      name: tier?.name ?? '',
      description: tier?.description ?? '',
      type: (tier?.type ?? 'STANDARD') as TierType,
      minimumTraffic: String(tier?.minimumTraffic ?? 100),
      maximumTraffic: String(tier?.maximumTraffic ?? 100),
    },
    onSubmit: ({ value }) => {
      mutation.mutate({
        name: value.name,
        description: value.description,
        type: value.type,
        minimumTraffic: Number(value.minimumTraffic),
        maximumTraffic: Number(value.maximumTraffic),
        objectState: (tier?.objectState ?? 'ACTIVE') as ObjectState,
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => TiersApi.update(id, { name: tier?.name ?? '', objectState }),
    ['tier', id],
    ['tiers']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  // A new tier joins the network's waterfall at the lowest priority. The tier
  // is saved first, so a failure here leaves it created but switched off.
  const enableInNetwork = async (tier: TierDetail): Promise<TierDetail> => {
    if (!currentNetwork) return tier
    try {
      const network = await TiersApi.setNetworkTiers(currentNetwork, [
        ...currentNetwork.tiers.map((networkTier) => networkTier.id),
        tier.id,
      ])
      queryClient.setQueryData(['network', network.tagId], network)
      // Saved again once enabled, so its warnings are current.
      return await TiersApi.getById(tier.id)
    } catch (error) {
      toast.error(t('tiers.enableFailed'), { description: apiErrorDescription(error), duration: 5000 })
      return tier
    }
  }

  const mutation = useMutation({
    mutationFn: async (input: TierInput) =>
      isNew ? enableInNetwork(await TiersApi.create(id, input)) : TiersApi.update(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['tier', id], data)
      queryClient.invalidateQueries({ queryKey: ['tiers'] })
      markClean()
      if (isNew) navigate(`/admin/tiers/${id}`, { replace: true })
      else stopEditing()
      toast.success(t('common.saved'), {
        description: isNew ? t('tiers.createdSuccess') : t('tiers.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError ? error.message : isNew ? t('tiers.createFailed') : t('tiers.saveFailed')
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const trafficValidator = ({ value }: { value: string }) =>
    isTrafficPercentage(value) ? undefined : t('tiers.trafficInvalid')

  const crumbs = [
    { label: t('tiers.title'), href: '/admin/tiers' },
    { label: isNew ? t('tiers.newTier') : (tier?.name ?? t('tiers.editTitle')) },
  ]

  return (
    <AppShell crumbs={crumbs} showBack>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={tier?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.tier')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('tiers.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={tier!.objectState} onChange={saveObjectState} allowDelete={!enabled} />
              {!isEditing && (
                <Button type="button" variant="outline" onClick={startEditing}>
                  <Pencil className="size-3.5" />
                  {t('common.edit')}
                </Button>
              )}
            </div>
          )}
        </div>

        {tier && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={tier.id} /> },
              { label: t('tiers.networkStatus'), content: <TierEnabledChip enabled={enabled} /> },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(tier.createTime)}
                    updated={formatDate(tier.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <div className="flex flex-col gap-3.5 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-3.5">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Layers} />
                  {t('tiers.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <div className="field-grid">
                    <div className="p-field full">
                      <FieldLabel>{t('common.description')}</FieldLabel>
                      <form.Field name="description">
                        {(field) => (
                          <Input
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder={t('tiers.descriptionPlaceholder')}
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={t('tiers.typeHelp')}>{t('common.type')}</FieldLabel>
                      <form.Field name="type">
                        {(field) => (
                          <Select
                            label={t('common.type')}
                            value={field.state.value}
                            onValueChange={(v) => field.handleChange(v as TierType)}
                          >
                            <SelectTrigger>
                              <SelectValue>{t(`tiers.types.${field.state.value}`)}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {TIER_TYPES.map((v) => (
                                <SelectItem key={v} value={v}>
                                  {t(`tiers.types.${v}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={t('tiers.minimumTrafficHelp')} hint={t('tiers.trafficHint')}>
                        {t('tiers.minimumTraffic')}
                      </FieldLabel>
                      <form.Field
                        name="minimumTraffic"
                        validators={{ onChange: trafficValidator, onSubmit: trafficValidator }}
                      >
                        {(field) => (
                          <>
                            <AmountInput
                              value={field.state.value}
                              onChange={(v) => field.handleChange(v)}
                              unit="%"
                              placeholder="100"
                              min={1}
                              max={100}
                              step={1}
                            />
                            {field.state.meta.errors[0] && (
                              <p className="text-xs text-destructive">{field.state.meta.errors[0] as string}</p>
                            )}
                          </>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={t('tiers.maximumTrafficHelp')} hint={t('tiers.trafficHint')}>
                        {t('tiers.maximumTraffic')}
                      </FieldLabel>
                      <form.Field
                        name="maximumTraffic"
                        validators={{ onChange: trafficValidator, onSubmit: trafficValidator }}
                      >
                        {(field) => (
                          <>
                            <AmountInput
                              value={field.state.value}
                              onChange={(v) => field.handleChange(v)}
                              unit="%"
                              placeholder="100"
                              min={1}
                              max={100}
                              step={1}
                            />
                            {field.state.meta.errors[0] && (
                              <p className="text-xs text-destructive">{field.state.meta.errors[0] as string}</p>
                            )}
                          </>
                        )}
                      </form.Field>
                    </div>
                  </div>
                ) : (
                  <DetailList>
                    <DetailRow label={t('common.description')}>{tier?.description}</DetailRow>
                    <DetailRow label={t('common.type')}>
                      {tier ? t(`tiers.types.${tier.type}`) : null}
                    </DetailRow>
                    <DetailRow label={t('tiers.minimumTraffic')}>
                      {tier ? `${formatNumber(tier.minimumTraffic)}%` : null}
                    </DetailRow>
                    <DetailRow label={t('tiers.maximumTraffic')}>
                      {tier ? `${formatNumber(tier.maximumTraffic)}%` : null}
                    </DetailRow>
                  </DetailList>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div
          className={`detail-savebar${isEditing || isDirty || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => (isNew ? navigate('/admin/tiers') : stopEditing())}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('tiers.newTier') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function TierEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const {
    data: tier,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['tier', id],
    queryFn: () => TiersApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('tiers.title'), href: '/admin/tiers' },
    { label: isNew ? t('tiers.newTier') : t('tiers.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={2} columns={2} />
      </AppShell>
    )
  }

  return <TierEditForm key={id} tier={tier ?? null} id={id!} isNew={isNew} />
}
